// @flow
import React from "react";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";

type $Props = {
  classes: Object,
};

const Moving = ({ classes }: $Props) => (
  <div className={classes.root}>
    <Typography type="body">
      We've laid out our Moving Tips timeline below. The timeline starts at
      eight weeks from your moving day and goes up until the day you arrive at
      your destination.
    </Typography>
    <img
      className={classes.inlineImage}
      src="https://storage.googleapis.com/elevate-images/elevate-tips-moving.jpg"
      align="right"
      alt="Couple hugging and holding keys to new home"
    />
    <Typography className={classes.subHeading} type="heading4">
      Eight weeks before you leave your present address
    </Typography>
    <ul>
      <li>
        Remove unnecessary items from your attic, basement,storage shed, etc.
        Use things you can't move, such as frozen foods and cleaning supplies.
      </li>
      <li>Obtain information about your new community.</li>
      <li>
        Secure a floor plan of your new residence and decide what household
        items you want to keep.
      </li>
      <li>Start a possessions inventory.</li>
      <li>Solicit estimates from at least three moving companies.</li>
      <li>
        Call your homeowners insurance agent to find out to what degree your
        move is covered.
      </li>
      <li>Create a file for documenting all moving papers and receipts.</li>
      <li>Arrange to transfer your children's school records.</li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Six weeks before you leave your present address
    </Typography>
    <ul>
      <li>Contact the IRS and/or your CPA for tax-deductible information.</li>
      <li>
        Evaluate your possessions inventory. Can you donate anything? Do you
        need it all?
      </li>
      <li>
        Notify your friends, relatives, professionals, creditors, subscriptions,
        etc.
      </li>
      <li>
        Subscribe to a local paper in your new community and familiarize
        yourself with local government, community and social news and
        activities.
      </li>
      <li>Begin the off-site storage process (if applicable).</li>
      <li>
        Locate high-quality health-care professionals and hospitals in your new
        location.
      </li>
      <li>
        Complete post-office change of address cards for the following: banks;
        charge cards; religious organizations; doctors/dentist; relatives and
        friends; income tax bureau/Social Security Administration/union;
        insurance broker/lawyer/CPA/ stockbroker; magazines; post office; and
        schools.
      </li>
      <li>Clean your closets.</li>
      <li>Hold a moving/garage sale or donate items to charities.</li>
      <li>
        Choose a mover. Contact your mover to make arrangements and inquire
        about insurance coverage.
      </li>
      <li>
        If relocating due to a job, contact your employer to see what costs, if
        any, they will cover.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Four weeks before you leave your present address
    </Typography>
    <ul>
      <li>Start packing!</li>
      <li>Send furniture, drapes and carpets for repair/cleaning as needed.</li>
      <li>
        Gather auto licensing and registration documents, medical, dental and
        school records, birth certificates, wills, deeds, stock and other
        financial documentation, etc.
      </li>
      <li>
        Contact gas, electric, oil, water, telephone, cable TV and trash
        collection companies for service disconnect /connect at your old and new
        addresses. Also ask for final readings.
      </li>
      <li>
        Request refunds on unused homeowner's insurance, security deposit with
        landlord, and prepaid cable/internet service.
      </li>
      <li>
        Notify your gardener, snow removal service and pool service (if
        applicable).
      </li>
      <li>
        Contact insurance companies (auto, homeowner's, medical and life) to
        arrange for coverage in your new home.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Three weeks before you leave your present address
    </Typography>
    <ul>
      <li>Make your travel plans.</li>
      <li>
        Arrange to close current bank accounts and open accounts in your new
        locale (if necessary).
      </li>
      <li>Notify your state's motor vehicle bureau of your new address.</li>
      <li>Arrange for childcare on moving day.</li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Two weeks before you leave your present address
    </Typography>
    <ul>
      <li>Arrange special transport for your pets and plants.</li>
      <li>Service your car for the trip.</li>
      <li>
        Contact your moving company and review arrangements for your move.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      One week before you leave your present address
    </Typography>
    <ul>
      <li>
        Prepare detailed directions and an itinerary with emergency numbers for
        your moving company.
      </li>
      <li>
        Settle outstanding bills with local retailers. Pick up dry cleaning, and
        return library books and rented videotapes.
      </li>
      <li>Take pets to the veterinarian and get copies of their records.</li>
      <li>Drain gas and oil from power equipment.</li>
      <li>Give away plants not being moved.</li>
      <li>Cancel newspaper delivery.</li>
      <li>
        Buy two-weeks worth of medication and have your prescriptions forwarded
        to your new pharmacy.
      </li>
      <li>Buy traveler's checks.</li>
      <li>Make arrangements to pay for your move.</li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Two to three days before you leave your present address
    </Typography>
    <ul>
      <li>If you're not doing it yourself, have your mover pack.</li>
      <li>Defrost refrigerators and freezers.</li>
      <li>
        Consider gathering all valuables and giving them to family or friends to
        hold until the move is completed.
      </li>
      <li>Disconnect all major appliances.</li>
      <li>Contact your moving company for any updates.</li>
      <li>
        Pack first-night items and a survival kit. Keep them in separate boxes
        in your car. First night items may include: sheets, towels, toiletries,
        phone, alarm clock, change of clothes and flashlight.
      </li>
      <li>
        Mover's survival kit may include: scissors, utility knife, coffee cups,
        instant coffee/tea or a coffee maker, water and soft drinks, snacks,
        paper plates, plastic utensils, paper towels, toilet paper, soap,
        pencils and paper, local phone book, masking and/or duct tape, trash
        bags, shelf liner and aspirin or ibuprofen.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      Moving day
    </Typography>
    <ul>
      <li>Be home to answer any questions your mover may have.</li>
      <li>Record all utility meter readings (gas, electric and water).</li>
      <li>Stay until your movers are finished.</li>
      <li>
        Complete information on the bill and carefully read the document and the
        inventory sheet before signing it.
      </li>
      <li>
        Keep your copies of the bill and inventory until your possessions are
        delivered, the charges are paid and any claims are settled.
      </li>
      <li>Take one final look around to see if you forgot anything.</li>
      <li>
        Give movers the directions to your new home, and an emergency number
        where you can be reached during the move.
      </li>
    </ul>
    <Typography className={classes.subHeading} type="heading4">
      At destination
    </Typography>
    <ul>
      <li>Unpack first-night items and mover's survival kit.</li>
      <li>
        Be at the destination to welcome the movers and be on hand to answer any
        questions.
      </li>
      <li>
        After the job is completed, pay what is owed. The driver is obligated by
        law (a federal requirement for interstate moves) to collect payment upon
        delivery.
      </li>
      <li>
        Scrutinize the unloading of your items and account for each one on your
        inventory sheet. Check promptly and carefully for any damaged or missing
        items.
      </li>
      <li>Place moving and other important documents in a safe place.</li>
      <li>Go to the post office and collect held mail.</li>
    </ul>
  </div>
);

const styles = (theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "20px auto",
    maxWidth: "660px",
    "& strong": {
      fontWeight: "bold",
    },
    "& p": {
      color: theme.colors.gray["700"],
      lineHeight: "1.6",
      marginBottom: theme.spacing.unit * 1.5,
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },
    },
    "& ul": {
      listStyleType: "disc",
      lineHeight: "1.6",
      color: theme.colors.gray["700"],
      margin: "20px 14px 20px 40px",
      fontSize: "18px",

      [theme.breakpoints(800)]: {
        fontSize: "20px",
      },

      "& li": {
        marginBottom: theme.spacing.unit * 1.5,
      },
    },
  },
  subHeading: {
    marginTop: theme.spacing.unit,
    marginBottom: theme.spacing.unit * 0.5,
    fontSize: "21px",

    [theme.breakpoints(800)]: {
      fontSize: "24px",
    },
  },
  inlineImage: {
    width: "100%",
    marginBottom: theme.spacing.unit * 1.5,
    display: "block",
    maxWidth: "100%",
    height: "auto",
    verticalAlign: "middle",
  },
});

export default withStyles(styles)(Moving);
